// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Key derivation and the Short Authentication String. Everything derives from
 * the room passphrase, which never leaves the device.
 *
 * The call itself is end-to-end encrypted without anything here: Meetwell is
 * full-mesh, so media travels browser-to-browser over DTLS-SRTP with no media
 * server in the path, and trystero encrypts the signalling handshake with the
 * same passphrase so a relay cannot read the SDP or swap DTLS fingerprints. The
 * SAS below lets two humans confirm out loud that they share that passphrase,
 * closing the loop against a relay that tries to bridge two different rooms.
 */
import { WORDS } from './wordlist.ts';

const enc = new TextEncoder();

async function sha256(...parts: Uint8Array[]): Promise<Uint8Array> {
  const total = parts.reduce((n, p) => n + p.byteLength, 0);
  const buf = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    buf.set(p, off);
    off += p.byteLength;
  }
  return new Uint8Array(await crypto.subtle.digest('SHA-256', buf as BufferSource));
}

/**
 * Derive the 32-byte room secret from the passphrase, salted by the room id, via
 * PBKDF2-SHA-256. This is the root of the SAS and the frame key.
 */
export async function deriveRoomSecret(pass: string, room: string): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey('raw', enc.encode(pass) as BufferSource, 'PBKDF2', false, ['deriveBits']);
  const salt = enc.encode(`meetwell:v1:${room}`) as BufferSource;
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations: 150_000, hash: 'SHA-256' },
    base,
    256,
  );
  return new Uint8Array(bits);
}

/**
 * Four SAS words, derived deterministically from (pass, room). Both peers derive
 * the SAME words from the shared secret, so reading them aloud confirms the two
 * sides really share the passphrase — a relay bridging two different rooms would
 * make the words disagree.
 */
export async function deriveSasWords(pass: string, room: string): Promise<string[]> {
  const secret = await deriveRoomSecret(pass, room);
  const digest = await sha256(secret, enc.encode('sas'));
  const out: string[] = [];
  for (let i = 0; i < 4; i++) out.push(WORDS[digest[i] % WORDS.length]);
  return out;
}

/** Hex fingerprint of the room secret — a compact, comparable trust anchor. */
export async function deriveFingerprint(pass: string, room: string): Promise<string> {
  const secret = await deriveRoomSecret(pass, room);
  const digest = await sha256(secret, enc.encode('fingerprint'));
  return Array.from(digest.subarray(0, 8))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
